import { Injectable, signal, computed } from '@angular/core';
import {
  DisclosureState, DisclosureProduct, BLANK_STATE, BLANK_PRODUCT,
  WizardStep, ReportType, ProductRole, SelectedRider
} from '../models/disclosure.model';
import { EvaluateResponse, InvestmentProfile, CompensationInfo } from '../models/exchange-eval.model';
import { Case } from '../../../core/services/case.model';
import { isValidName } from '../utils/name-validation';

@Injectable({ providedIn: 'root' })
export class DisclosureStateService {
  private readonly _state = signal<DisclosureState>({ ...BLANK_STATE });
  readonly state = this._state.asReadonly();

  readonly step = computed(() => this._state().step);
  readonly reportType = computed(() => this._state().reportType);
  readonly isExchange = computed(() => this._state().reportType === 'exchange');
  readonly products = computed(() => this._state().products);
  readonly exchangeEvaluation = computed(() => this._state().exchangeEvaluation);

  /** The client's state from step 1, normalized to a 2-letter code (empty when
   *  not yet chosen). Product pickers scope their catalog to it, so it is
   *  normalized here rather than at each call site. */
  readonly clientState = computed(() => (this._state().state ?? '').trim().toUpperCase());

  readonly exchangedProduct = computed(() =>
    this._state().products.find(p => p.role === 'exchange') ?? null
  );
  readonly recommendedProducts = computed(() =>
    this._state().products.filter(p => p.role === 'recommend')
  );

  /** Client/advisor name are optional, but once entered must be a valid name
   *  (see name-validation.ts) — never advance past a value the Information
   *  step is showing as invalid. */
  readonly canProceedToStep2 = computed(() => {
    const s = this._state();
    return isValidName(s.clientName) && isValidName(s.advisorName);
  });
  readonly canProceedToStep3 = computed(() => {
    const s = this._state();
    // A sales disclosure has no existing contract to pair against, so one
    // selected product is enough to continue. An exchange disclosure still
    // needs both the exchanged contract and at least one recommended product.
    const minProducts = s.reportType === 'sales' ? 1 : 2;
    return s.products.length >= minProducts
      && s.products.every(p => p.productId !== null)
      && s.products.every(p => p.role !== 'exchange' || !!p.contractDate);
  });

  setStep(step: WizardStep): void {
    this._state.update(s => ({ ...s, step }));
  }

  /** Briefly highlights Product Selection's drop boxes. Set from the basket's
   *  "Drag" buttons — the basket is an app-shell component, so it can't reach
   *  the step component directly. UI-only; not part of the saved state. */
  readonly dropZoneHint = signal(false);
  private dropZoneHintTimer: ReturnType<typeof setTimeout> | null = null;

  flashDropZones(ms = 2500): void {
    if (this.dropZoneHintTimer) clearTimeout(this.dropZoneHintTimer);
    this.dropZoneHint.set(true);
    this.dropZoneHintTimer = setTimeout(() => this.dropZoneHint.set(false), ms);
  }

  setReportType(reportType: ReportType): void {
    this._state.update(s => {
      // Re-clicking the already-selected type is a no-op click, not a change
      // — leave product selection alone.
      if (s.reportType === reportType) return s;
      // Product Selection is scoped to the report type (the exchanged-role
      // card only exists for an Exchange disclosure, and which products are
      // even relevant can differ between the two). Rather than leave stale
      // selections silently satisfying canProceedToStep3()/readyToGenerate()
      // — or showing products picked under the previous type — start Product
      // Selection over whenever the type actually changes.
      return { ...s, reportType, products: [] };
    });
  }

  updateInfo(partial: Partial<Pick<DisclosureState,
    'clientName' | 'advisorName' | 'state' | 'lastExchangeDate' |
    'customerAge' | 'customerGender' | 'contractValue' | 'benefitBase' |
    'purchasePayment' | 'producerRationale'
  >>): void {
    this._state.update(s => ({ ...s, ...partial }));
  }

  updateInvestmentProfile(partial: Partial<InvestmentProfile>): void {
    this._state.update(s => ({
      ...s,
      investmentProfile: { ...s.investmentProfile, ...partial }
    }));
  }

  updateCompensation(partial: Partial<CompensationInfo>): void {
    this._state.update(s => ({
      ...s,
      compensation: { ...s.compensation, ...partial }
    }));
  }

  /** Ever-incrementing — only needs to be unique among currently-live
   *  products, not globally forever, so no reset-on-clear is needed. */
  private nextProductUid = 0;

  addProduct(role: ProductRole): void {
    const uid = `p${++this.nextProductUid}`;
    this._state.update(s => ({
      ...s,
      products: [...s.products, { ...BLANK_PRODUCT, role, uid }]
    }));
  }

  updateProduct(index: number, partial: Partial<DisclosureProduct>): void {
    this._state.update(s => {
      const products = [...s.products];
      products[index] = { ...products[index], ...partial };
      return { ...s, products };
    });
  }

  removeProduct(index: number): void {
    this._state.update(s => ({
      ...s,
      products: s.products.filter((_, i) => i !== index)
    }));
  }

  /** At most one selected rider per benefit type (Death Benefit, GMWB, GMIB,
   *  GMAB, Bonus, Feature) per product. Selecting a new one in a type that
   *  already has a selection replaces it rather than adding to it — same
   *  checkbox UI, but it behaves like an exclusive group per type. */
  toggleRider(productIndex: number, rider: SelectedRider): void {
    this._state.update(s => {
      const products = [...s.products];
      const product = { ...products[productIndex] };
      const idx = product.selectedRiderIds.indexOf(rider.id);
      if (idx >= 0) {
        product.selectedRiderIds = product.selectedRiderIds.filter(id => id !== rider.id);
        product.selectedRiders = (product.selectedRiders ?? []).filter(r => r.id !== rider.id);
      } else {
        const dropIds = new Set(
          (product.selectedRiders ?? []).filter(r => r.type === rider.type).map(r => r.id)
        );
        product.selectedRiderIds = [...product.selectedRiderIds.filter(id => !dropIds.has(id)), rider.id];
        product.selectedRiders = [...(product.selectedRiders ?? []).filter(r => r.type !== rider.type), rider];
      }
      products[productIndex] = product;
      return { ...s, products };
    });
  }

  setEvaluation(evaluation: EvaluateResponse): void {
    this._state.update(s => ({ ...s, exchangeEvaluation: evaluation }));
  }

  reset(): void {
    this._state.set({ ...BLANK_STATE });
  }

  readonly resetRequest = signal(0);
  requestReset(): void {
    this.resetRequest.update((n) => n + 1);
  }

  replaceState(next: DisclosureState): void {
    this._state.set({ ...next });
  }

  snapshot(): DisclosureState {
    return this._state();
  }

  prepopulateFromCase(c: Case): void {
    this._state.update(s => ({
      ...s,
      clientName:     s.clientName     || c.name || '',
      state:          s.state          || c.state || '',
      customerAge:    s.customerAge    ?? c.age,
      customerGender: s.customerGender ?? c.gender,
      contractValue:  s.contractValue  ?? c.premium,
      purchasePayment: s.purchasePayment ?? c.premium,
    }));
  }

  toCaseInput(): { name: string; state: string | null; age: number | null; premium: number | null } {
    const s = this._state();
    return {
      name: s.clientName.trim() || 'Disclosure case',
      state: s.state || null,
      age: s.customerAge,
      premium: s.contractValue ?? s.purchasePayment ?? null,
    };
  }
}
